import React from 'react'
import ChartFlexSection from '@components/chart/ChartFlexSection'
import { IResponseOptProjectBudgetByQuarter } from '@interfaces/api/IDashboard'
import TextChartTitle from '@components/text/TextChartTitle'
import { Money, Num, ShortMoney, ThY } from '@utils/utils'
import Chart from 'react-google-charts'
import { ColumnChartOptions } from '@components/chart/setting'
import { Table } from '@mantine/core'

const quarters = ['Q1', 'Q2', 'Q3', 'Q4']

const ProjectBudgetByQuarter = ({ title, data }: { title: string; data: IResponseOptProjectBudgetByQuarter }) => {
  const options = ColumnChartOptions('มูลค่าโครงการ', 'ไตรมาสปีงบประมาณ (Quarter)')

  // Group data by budget year
  const grouped: Record<
    string,
    {
      [q in 'Q1' | 'Q2' | 'Q3' | 'Q4']?: {
        total_budget: number
        total_projects: number
      }
    }
  > = {}

  data.forEach((item) => {
    const year = String(ThY(item.budget_year))
    if (!grouped[year]) grouped[year] = {}
    grouped[year][item.quarter as 'Q1' | 'Q2' | 'Q3' | 'Q4'] = {
      total_budget: parseFloat(item.total_budget),
      total_projects: item.total_projects,
    }
  })

  const chartData = [
    [
      'ปีงบประมาณ',
      'Q1',
      { type: 'string', role: 'annotation' },
      { role: 'tooltip', type: 'string', p: { html: true } },
      'Q2',
      { type: 'string', role: 'annotation' },
      { role: 'tooltip', type: 'string', p: { html: true } },
      'Q3',
      { type: 'string', role: 'annotation' },
      { role: 'tooltip', type: 'string', p: { html: true } },
      'Q4',
      { type: 'string', role: 'annotation' },
      { role: 'tooltip', type: 'string', p: { html: true } },
    ],
    ...Object.entries(grouped).map(([year, qData]) => {
      const row: (string | number | null)[] = [`${year} \n (Q1-Q4)`]
      quarters.forEach((q, i) => {
        const dataPoint = qData[q as 'Q1' | 'Q2' | 'Q3' | 'Q4']
        const budget = dataPoint?.total_budget ?? 0
        const projects = dataPoint?.total_projects ?? 0
        row.push(budget)
        row.push(ShortMoney(budget))
        row.push(`<div style="padding:8px; width: 280px">
            <strong>ปีงบประมาณ ${year} ไตรมาสที่ ${q[1]}</strong><br/>
            จำนวนโครงการ : ${projects} โครงการ<br/>
            มูลค่าโครงการ : ${Money(budget)} บาท
          </div>
        `)
      })
      return row
    }),
  ]

  const rows = Object.keys(grouped).map((year) => (
    <tr key={year}>
      <td style={{ textAlign: 'center' }}>{year}</td>
      <td style={{ textAlign: 'center' }}>{Num(grouped[year].Q1?.total_projects ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Money(grouped[year].Q1?.total_budget ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Num(grouped[year].Q2?.total_projects ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Money(grouped[year].Q2?.total_budget ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Num(grouped[year].Q3?.total_projects ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Money(grouped[year].Q3?.total_budget ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Num(grouped[year].Q4?.total_projects ?? 0)}</td>
      <td style={{ textAlign: 'center' }}>{Money(grouped[year].Q4?.total_budget ?? 0)}</td>
    </tr>
  ))

  return (
    <ChartFlexSection>
      <TextChartTitle>{title}</TextChartTitle>
      <Table maw={1300} width="fit" style={{ width: 'auto', tableLayout: 'auto' }} withBorder withColumnBorders striped>
        <thead>
          <tr>
            <th color="black" style={{ textAlign: 'center' }} align="center" rowSpan={2}>
              ปีงบประมาณ
            </th>
            <th style={{ textAlign: 'center' }} colSpan={2}>
              Q1
            </th>
            <th style={{ textAlign: 'center' }} colSpan={2}>
              Q2
            </th>
            <th style={{ textAlign: 'center' }} colSpan={2}>
              Q3
            </th>
            <th style={{ textAlign: 'center' }} colSpan={2}>
              Q4
            </th>
          </tr>
          <tr>
            <th style={{ textAlign: 'center' }}>จำนวนโครงการ</th>
            <th style={{ textAlign: 'center' }}>มูลค่าโครงการ</th>
            <th style={{ textAlign: 'center' }}>จำนวนโครงการ</th>
            <th style={{ textAlign: 'center' }}>มูลค่าโครงการ</th>
            <th style={{ textAlign: 'center' }}>จำนวนโครงการ</th>
            <th style={{ textAlign: 'center' }}>มูลค่าโครงการ</th>
            <th style={{ textAlign: 'center' }}>จำนวนโครงการ</th>
            <th style={{ textAlign: 'center' }}>มูลค่าโครงการ</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </Table>
      <Chart width={'100%'} height={'500px'} chartType="ColumnChart" loader={<div>Loading Chart</div>} data={chartData} options={options} />
    </ChartFlexSection>
  )
}

export default ProjectBudgetByQuarter
